<template>
    <ContentComp
        :fill-content="true"
        class="p-0 m-0"
    >
        <AlertMessage
            v-if="value.Bib === null && false"
            type="info"
            >{{ $tm('racecontestantcomplete.contestantneedbibforchip') }}</AlertMessage
        >
        <FormLayout :label-width="160">
            <FormLayoutRow>
                <div class="d-flex flex-fill flex-row flex-wrap">
                    <div class="w-xxl-50 mb-3 mb-xxl-0">
                        <div class="d-md-flex align-items-sm-center">
                            <FormLayoutLabel>{{ $tm('general.bib') }}</FormLayoutLabel>
                            <div class="w-abs-150">
                                <InputNullableInt
                                    v-model.lazy="value.Bib"
                                    :class="{ 'cell-dirty': isBibChanged }"
                                />
                            </div>

                            <FormLayoutLabel>{{ $tm('racecontestantcomplete.chip1') }}</FormLayoutLabel>
                            <div class="d-flex flex-fill w-abs-150">
                                <InputText
                                    v-model="value.Chip1"
                                    :disabled="value.Bib === null"
                                />
                                <ButtonComp
                                    :disabled="value.Bib === null || value.ParticipantID === 0"
                                    button-type="Secondary"
                                    :loadstate="LoadState"
                                    @click="getEmitId"
                                    icon="fa-refresh"
                                    >{{ $tm('chip.getfromemit') }}</ButtonComp
                                >
                            </div>
                        </div>
                    </div>
                    <div class="w-xxl-50">
                        <div class="d-md-flex align-items-sm-center">
                            <FormLayoutLabel>{{ $tm('racecontestantcomplete.chip2') }}</FormLayoutLabel>
                            <div class="w-abs-150">
                                <InputText
                                    v-model="value.Chip2"
                                    :disabled="isBibChanged || value.Bib === null"
                                />
                            </div>

                            <FormLayoutLabel>{{ $tm('racecontestantcomplete.seeding') }}</FormLayoutLabel>
                            <div class="w-abs-150">
                                <InputNullableFloat v-model="value.Seeding" />
                            </div>
                        </div>
                    </div>
                </div>
            </FormLayoutRow>
        </FormLayout>
    </ContentComp>
</template>

<script setup lang="ts">
import { AlertMessage, ButtonComp, InputNullableFloat, InputText } from '@/components';
import InputNullableInt from '@/components/Basic/InputNullableInt.vue';
import { FormLayout, FormLayoutLabel, FormLayoutRow } from '@/components/Form';
import ContentComp from '@/components/layout/ContentComp.vue';
import { useDialog, useTaskLoader } from '@/composeables';
import { ContestantDao, ParticipantDao } from '@/dao';
import { IntArray, RaceContestantDetails } from '@/objects/generated';
import WellKnown from '@/objects/generated/static/WellKnown';
import { useToastStore } from '@/stores';
import { watchDebounced } from '@vueuse/core';
import { ref, watch } from 'vue';
import ContestantBibHandleView from './ContestantBibHandleView.vue';

const value = defineModel<RaceContestantDetails>({ required: true });

// Holds the original bib value
const originalBibValue = value.value.Bib;

// Indicates if the bib value has changed
const isBibChanged = ref(false);

const { AddLoader, LoadState } = useTaskLoader();

/**
 * Will fetch the registered emit id for the participant
 * TODO: This is not yet implemented.
 */
const getEmitId = () => {
    if ((value.value.ParticipantID ?? 0) == 0) {
        return;
    }
    AddLoader(async () => {
        let ids = IntArray.FromJsValues({
            Values: [WellKnown.IDTYPE_EMIT],
        });
        try {
            let emitid = await ParticipantDao.GetParticipantIDForTypes(ids, value.value.ParticipantID);
            if (emitid == null || emitid.length == 0 || emitid[0].Value == null) {
                throw new Error('No emit id found');
            }
            value.value.Chip1 = emitid[0].Value;
        } catch (e) {
            useToastStore().addInfo('emitsearch.notfound');
        }
    }, 'emitsearch.searching');
};

watchDebounced(
    () => value.value.Bib,
    async (newVal, oldVal) => {
        // We need to check if the bib is allready in use
        if (newVal === null) {
            return;
        }
        let conflictContestant = await ContestantDao.GetContestantByBib(value.value.EventID, newVal);

        if (conflictContestant != null && conflictContestant.ID !== value.value.ContestantID) {
            useDialog().SaveCancel({
                title: 'contestantdetails.bibconflicttitle',
                body: ContestantBibHandleView,
                componentProps: { conflictedcontestant: conflictContestant },
                saveCallback: async () => true,
                cancelCallback: async () => {
                    // We reset the bib value to null
                    value.value.Bib = null;
                    return false;
                },
                vmodel: value,
                options: {
                    Size: 'md',
                    DisableAnimation: true,
                },
            });
        }
    },
    { debounce: 500 },
);

// Watch the bib value for changes
watch(value.value, (newValue) => (isBibChanged.value = newValue.Bib != originalBibValue));
</script>
